import React, { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Save, X, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

const emptyItem = () => ({
  descripcion: "", unidad: "Pieza", cantidad: "", precio_unitario: "",
});

function fmtMoney(v) {
  return new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(v || 0);
}

export default function CotizacionesACEditPage() {
  const { cotId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [form, setForm] = useState({
    cliente: "", contacto: "", fecha: "", vigencia: "",
    condiciones_pago: "", items: [emptyItem()], indicaciones: [""], notas: "",
  });

  const { data: cot, isLoading } = useQuery({
    queryKey: ["cotizacion-ac", cotId],
    queryFn: async () => (await api.get(`/cotizaciones-ac/${cotId}`)).data,
    enabled: !!cotId,
  });

  useEffect(() => {
    if (cot && !loaded) {
      setForm({
        cliente: cot.cliente || "",
        contacto: cot.contacto || "",
        fecha: cot.fecha || "",
        vigencia: cot.vigencia || "",
        condiciones_pago: cot.condiciones_pago || "",
        items: (cot.items || []).length > 0
          ? cot.items.map(it => ({
              descripcion: it.descripcion || "",
              unidad: it.unidad || "Pieza",
              cantidad: String(it.cantidad || ""),
              precio_unitario: String(it.precio_unitario || ""),
            }))
          : [emptyItem()],
        indicaciones: (cot.indicaciones || []).length > 0 ? [...cot.indicaciones] : [""],
        notas: cot.notas || "",
      });
      setLoaded(true);
    }
  }, [cot, loaded]);

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const updateItem = (idx, field, value) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [field]: value };
    setForm(prev => ({ ...prev, items }));
  };
  const addItem = () => setForm(prev => ({ ...prev, items: [...prev.items, emptyItem()] }));
  const removeItem = (idx) => {
    if (form.items.length <= 1) return;
    setForm(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));
  };

  const updateIndicacion = (idx, val) => {
    const indicaciones = [...form.indicaciones];
    indicaciones[idx] = val;
    setForm(prev => ({ ...prev, indicaciones }));
  };
  const addIndicacion = () => setForm(prev => ({ ...prev, indicaciones: [...prev.indicaciones, ""] }));
  const removeIndicacion = (idx) => {
    if (form.indicaciones.length <= 1) return;
    setForm(prev => ({ ...prev, indicaciones: prev.indicaciones.filter((_, i) => i !== idx) }));
  };

  const totals = useMemo(() => {
    let subtotal = 0;
    form.items.forEach(it => {
      const qty = parseFloat(it.cantidad) || 0;
      const price = parseFloat(it.precio_unitario) || 0;
      subtotal += qty * price;
    });
    subtotal = Math.round(subtotal * 100) / 100;
    const iva = Math.round(subtotal * 0.16 * 100) / 100;
    const total = Math.round((subtotal + iva) * 100) / 100;
    return { subtotal, iva, total };
  }, [form.items]);

  const handleSubmit = async () => {
    if (!form.cliente.trim()) {
      toast.error("El cliente es requerido");
      return;
    }
    const validItems = form.items.filter(it => it.descripcion.trim());
    if (validItems.length === 0) {
      toast.error("Agrega al menos un concepto con descripción");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        items: validItems.map(it => ({
          descripcion: it.descripcion,
          unidad: it.unidad || "Pieza",
          cantidad: parseFloat(it.cantidad) || 0,
          precio_unitario: parseFloat(it.precio_unitario) || 0,
        })),
        indicaciones: form.indicaciones.filter(i => i.trim()),
      };
      await api.patch(`/cotizaciones-ac/${cotId}`, payload);
      toast.success("Cotización actualizada");
      qc.invalidateQueries({ queryKey: ["cotizacion-ac", cotId] });
      qc.invalidateQueries({ queryKey: ["cotizaciones-ac"] });
      navigate(`/ots-comercial/cotizaciones/${cotId}`);
    } catch (e) {
      const msg = e.response?.data?.detail || "Error al actualizar";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-[1100px]" data-testid="cotizacion-edit-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" asChild>
          <Link to={`/ots-comercial/cotizaciones/${cotId}`} data-testid="cotizacion-edit-back">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-xl font-semibold tracking-tight flex items-center gap-2">
            Editar Cotización
            <Badge variant="outline" className="font-mono text-base">{cot?.folio || ""}</Badge>
          </h1>
          <p className="text-sm text-muted-foreground">Modifica los datos de la cotización.</p>
        </div>
      </div>

      {/* General data */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Datos Generales</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground">Cliente *</Label>
              <Input value={form.cliente} onChange={(e) => setField("cliente", e.target.value)} data-testid="cotizacion-edit-cliente" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Contacto</Label>
              <Input value={form.contacto} onChange={(e) => setField("contacto", e.target.value)} data-testid="cotizacion-edit-contacto" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Fecha</Label>
              <Input type="date" value={form.fecha} onChange={(e) => setField("fecha", e.target.value)} data-testid="cotizacion-edit-fecha" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Vigencia</Label>
              <Input value={form.vigencia} onChange={(e) => setField("vigencia", e.target.value)} data-testid="cotizacion-edit-vigencia" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Condiciones de Pago</Label>
              <Input value={form.condiciones_pago} onChange={(e) => setField("condiciones_pago", e.target.value)} data-testid="cotizacion-edit-condiciones" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Items */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm">Conceptos / Servicios</CardTitle>
            <Button size="sm" variant="outline" onClick={addItem} data-testid="cotizacion-edit-add-item">
              <Plus className="h-3.5 w-3.5 mr-1" /> Agregar
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[650px]">
              <thead className="bg-muted/40">
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 px-3 w-8">#</th>
                  <th className="py-2 px-3">Descripción *</th>
                  <th className="py-2 px-3 w-24">Unidad</th>
                  <th className="py-2 px-3 w-24">Cantidad</th>
                  <th className="py-2 px-3 w-32">Precio Unitario</th>
                  <th className="py-2 px-3 w-32 text-right">Importe</th>
                  <th className="py-2 px-3 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {form.items.map((it, idx) => {
                  const qty = parseFloat(it.cantidad) || 0;
                  const price = parseFloat(it.precio_unitario) || 0;
                  const importe = Math.round(qty * price * 100) / 100;
                  return (
                    <tr key={idx} className="border-t border-border/60">
                      <td className="py-2 px-3 text-muted-foreground text-center">{idx + 1}</td>
                      <td className="py-2 px-3">
                        <Input value={it.descripcion} onChange={(e) => updateItem(idx, "descripcion", e.target.value)} className="h-8 text-sm" data-testid={`cotizacion-edit-item-desc-${idx}`} />
                      </td>
                      <td className="py-2 px-3">
                        <Input value={it.unidad} onChange={(e) => updateItem(idx, "unidad", e.target.value)} className="h-8 text-sm" />
                      </td>
                      <td className="py-2 px-3">
                        <Input type="number" min="0" value={it.cantidad} onChange={(e) => updateItem(idx, "cantidad", e.target.value)} className="h-8 text-sm font-mono tabular-nums" />
                      </td>
                      <td className="py-2 px-3">
                        <Input type="number" min="0" step="0.01" value={it.precio_unitario} onChange={(e) => updateItem(idx, "precio_unitario", e.target.value)} className="h-8 text-sm font-mono tabular-nums" />
                      </td>
                      <td className="py-2 px-3 text-right font-mono tabular-nums font-medium">{fmtMoney(importe)}</td>
                      <td className="py-2 px-3">
                        {form.items.length > 1 && (
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => removeItem(idx)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end border-t border-border p-4">
            <div className="w-72 space-y-1.5">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal:</span>
                <span className="font-mono tabular-nums">{fmtMoney(totals.subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">IVA (16%):</span>
                <span className="font-mono tabular-nums">{fmtMoney(totals.iva)}</span>
              </div>
              <div className="flex justify-between text-base font-semibold border-t border-border pt-1.5">
                <span>Total:</span>
                <span className="font-mono tabular-nums text-primary">{fmtMoney(totals.total)}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Indicaciones */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm">Indicaciones / Incluye</CardTitle>
            <Button size="sm" variant="outline" onClick={addIndicacion} data-testid="cotizacion-edit-add-indicacion">
              <Plus className="h-3.5 w-3.5 mr-1" /> Agregar
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {form.indicaciones.map((ind, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <Badge variant="secondary" className="shrink-0 text-[10px] w-6 h-6 justify-center p-0">{idx + 1}</Badge>
                <Input value={ind} onChange={(e) => updateIndicacion(idx, e.target.value)} className="h-8 text-sm" data-testid={`cotizacion-edit-indicacion-${idx}`} />
                {form.indicaciones.length > 1 && (
                  <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0 text-destructive" onClick={() => removeIndicacion(idx)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Notas */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Notas</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea value={form.notas} onChange={(e) => setField("notas", e.target.value)} className="min-h-[80px]" data-testid="cotizacion-edit-notas" />
        </CardContent>
      </Card>

      {/* Footer */}
      <div className="flex justify-end gap-3 pb-6">
        <Button variant="outline" asChild data-testid="cotizacion-edit-cancel">
          <Link to={`/ots-comercial/cotizaciones/${cotId}`}>
            <X className="h-4 w-4 mr-1" /> Cancelar
          </Link>
        </Button>
        <Button onClick={handleSubmit} disabled={saving} data-testid="cotizacion-edit-save">
          <Save className="h-4 w-4 mr-1" /> {saving ? "Guardando..." : "Guardar Cambios"}
        </Button>
      </div>
    </div>
  );
}
